import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import md5 from 'js-md5';

import { createUser, fetchUserDetail, updateUser } from '#/api/system/user';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import FloatingSelect from '#/modules/commonComponents/form/select/FloatingSelect';
import { ENTITY_STATUS_ACTIVE_INACTIVE_OPTIONS } from '#/modules/commonComponents/table/entityStatus';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  USER_FIELD_LABELS,
  userCreateSchema,
  userEditSchema,
} from '#/modules/infra/settings/access/schemas/userSchema';

const USER_LIST_QUERY_KEY_PREFIX = () => ['system', 'user', 'list'];
const userDetailQueryKey = (id) => ['system', 'user', 'detail', id];

const EMPTY_VALUES = {
  accountNo: '',
  account: '',
  name: '',
  chnName: '',
  engName: '',
  workEmail: '',
  password: '',
  status: 'ACTIVE',
  remark: '',
};

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function emptyToNull(value) {
  const trimmed = (value ?? '').trim();
  return trimmed || null;
}

const CreateEditUserPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const canCreate = useCan(PERM.SYS_ACCOUNT_CREATE);
  const canUpdate = useCan(PERM.SYS_ACCOUNT_UPDATE);
  const canReadAccount = useCan(PERM.SYS_ACCOUNT_READ);
  const canReadRole = useCan(PERM.SYS_ROLE_READ);
  const canOpenAccess = canReadAccount && canReadRole;
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: userDetailQueryKey(id),
    queryFn: () => fetchUserDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createUser,
    updateFn: async (userId, payload) => {
      const result = await updateUser(userId, payload);
      const nextUpdatedAt = result?.data?.updatedAt ?? result?.data?.record?.updatedAt;
      if (nextUpdatedAt) {
        updatedAtRef.current = nextUpdatedAt;
      }
      return result;
    },
    buildPayload: (values, { isEdit: editing }) => {
      const shared = {
        name: values.name.trim(),
        chnName: emptyToNull(values.chnName),
        engName: emptyToNull(values.engName),
        workEmail: emptyToNull(values.workEmail),
        remark: emptyToNull(values.remark),
      };

      if (editing) {
        return { ...shared, status: values.status, updatedAt: updatedAtRef.current };
      }

      return {
        ...shared,
        accountNo: values.accountNo.trim(),
        account: values.account.trim(),
        password: md5(values.password),
      };
    },
    listQueryKeyPrefix: USER_LIST_QUERY_KEY_PREFIX,
    detailQueryKey: userDetailQueryKey,
    getEditPath: ROUTES.systemUserEdit,
    getSuccessMessage: (_result, { isEdit: editing }) => (editing ? '帳號已儲存' : '帳號已建立'),
    getErrorMessage: (error) => getErrorMessage(error, '儲存失敗'),
  });

  const canWrite = isEdit ? canUpdate : canCreate;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(isEdit ? userEditSchema : userCreateSchema),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset({
      ...EMPTY_VALUES,
      accountNo: detail.accountNo ?? '',
      account: detail.account ?? '',
      name: detail.name ?? '',
      chnName: detail.chnName ?? '',
      engName: detail.engName ?? '',
      workEmail: detail.workEmail ?? '',
      status: detail.status || 'ACTIVE',
      remark: detail.remark ?? '',
    });
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯帳號' : '新增帳號';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const readOnly = !canWrite;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-3xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.systemUsers}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getErrorMessage(detailQuery.error, '找不到帳號')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={USER_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body">
                  <div className={cn('grid grid-cols-1 gap-x-4 gap-y-4', '@xl/sheet:grid-cols-2')}>
                    <FloatingInput
                      id="accountNo"
                      type="text"
                      label="帳號編號"
                      register={register}
                      errors={errors}
                      readOnly={isEdit || readOnly}
                      inputClassName="font-mono"
                    />
                    <FloatingInput
                      id="account"
                      type="text"
                      label="登入帳號"
                      register={register}
                      errors={errors}
                      readOnly={isEdit || readOnly}
                      inputClassName="font-mono"
                      autoComplete="off"
                    />
                    <FloatingInput
                      id="name"
                      type="text"
                      label="姓名"
                      register={register}
                      errors={errors}
                      readOnly={readOnly}
                    />
                    {isEdit ? (
                      <FloatingSelect
                        id="status"
                        label="狀態"
                        options={ENTITY_STATUS_ACTIVE_INACTIVE_OPTIONS}
                        control={control}
                        errors={errors}
                        isDisabled={readOnly}
                      />
                    ) : (
                      <FloatingInput
                        id="password"
                        type="password"
                        label="密碼"
                        register={register}
                        errors={errors}
                        isVisible
                        autoComplete="new-password"
                      />
                    )}
                    <FloatingInput
                      id="chnName"
                      type="text"
                      label="中文名"
                      register={register}
                      errors={errors}
                      readOnly={readOnly}
                    />
                    <FloatingInput
                      id="engName"
                      type="text"
                      label="英文名"
                      register={register}
                      errors={errors}
                      readOnly={readOnly}
                    />
                    <FloatingInput
                      id="workEmail"
                      type="email"
                      label="工作信箱"
                      register={register}
                      errors={errors}
                      readOnly={readOnly}
                      autoComplete="email"
                      containerClassName="@xl/sheet:col-span-2"
                    />
                    <FloatingTextarea
                      id="remark"
                      label="備註"
                      register={register}
                      errors={errors}
                      readOnly={readOnly}
                      containerClassName="@xl/sheet:col-span-2"
                    />
                  </div>
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  {isEdit && canOpenAccess ? (
                    <Button type="button" variant="outline" className="mr-auto" asChild>
                      <Link to={ROUTES.systemUserAccessFor(id)}>存取設定</Link>
                    </Button>
                  ) : null}
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.systemUsers}>取消</Link>
                  </Button>
                  {canWrite ? (
                    <Button type="submit" disabled={isPending}>
                      {isPending ? '儲存中…' : '儲存'}
                    </Button>
                  ) : null}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditUserPage;
